'use client';

import { useTransition } from 'react';
import {
  Building2,
  Calendar,
  CheckCircle2,
  ExternalLink,
  MapPin,
  User,
  UserMinus,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Separator } from '@kit/ui/separator';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';

import type {
  MeetingPriority,
  MeetingRequestedBy,
  MeetingStatus,
  MeetingWithDetails,
} from '../../services/meeting.service';

interface MeetingDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: MeetingWithDetails | null;
  viewAs?: 'organization' | 'dancer';
  onConfirm?: (
    meetingId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  onCancel?: (
    meetingId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  onComplete?: (
    meetingId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  onMarkNoShow?: (
    meetingId: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Modal for viewing meeting details with action buttons
 *
 * Features:
 * - Full meeting details
 * - Dancer/organization info
 * - Status change actions
 * - Notes display (shared and private)
 */
export function MeetingDetailsModal({
  open,
  onOpenChange,
  meeting,
  viewAs = 'organization',
  onConfirm,
  onCancel,
  onComplete,
  onMarkNoShow,
}: MeetingDetailsModalProps) {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();

  if (!meeting) return null;

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: MeetingStatus) => {
    const config: Record<
      MeetingStatus,
      {
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
        label: string;
      }
    > = {
      pending: {
        variant: 'secondary',
        label: t('profiles:meetings.status.pending'),
      },
      confirmed: {
        variant: 'default',
        label: t('profiles:meetings.status.confirmed'),
      },
      cancelled: {
        variant: 'destructive',
        label: t('profiles:meetings.status.cancelled'),
      },
      completed: {
        variant: 'outline',
        label: t('profiles:meetings.status.completed'),
      },
      no_show: {
        variant: 'destructive',
        label: t('profiles:meetings.status.noShow'),
      },
    };
    const { variant, label } = config[status];
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityLabel = (priority: MeetingPriority) => {
    return priority === 'high'
      ? t('profiles:meetings.priority.high')
      : t('profiles:meetings.priority.normal');
  };

  const getRequestedByLabel = (requestedBy: MeetingRequestedBy) => {
    return requestedBy === 'organization'
      ? t('profiles:meetings.requestedBy.organization')
      : t('profiles:meetings.requestedBy.dancer');
  };

  const isPast = new Date(meeting.scheduled_at) < new Date();
  const canConfirm = meeting.status === 'pending';
  const canCancel =
    meeting.status === 'pending' || meeting.status === 'confirmed';
  const canComplete = meeting.status === 'confirmed' && isPast;
  const canMarkNoShow = meeting.status === 'confirmed' && isPast;

  const handleAction = async (
    action: (
      meetingId: string,
    ) => Promise<{ success: boolean; error?: string }>,
    successMessage: string,
  ) => {
    startTransition(async () => {
      const result = await action(meeting.id);
      if (result.success) {
        toast.success(successMessage);
        onOpenChange(false);
      } else {
        toast.error(result.error ?? t('profiles:meetings.toast.error'));
      }
    });
  };

  const showOtherParty =
    viewAs === 'organization' ? meeting.dancer : meeting.organization;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-4">
            {showOtherParty && (
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={
                    viewAs === 'organization'
                      ? (meeting.dancer?.avatar_url ?? undefined)
                      : (meeting.organization?.logo_url ?? undefined)
                  }
                  alt={
                    viewAs === 'organization'
                      ? `${meeting.dancer?.first_name} ${meeting.dancer?.last_name}`
                      : (meeting.organization?.name ?? '')
                  }
                />
                <AvatarFallback>
                  {viewAs === 'organization' ? (
                    <User className="h-6 w-6" />
                  ) : (
                    <Building2 className="h-6 w-6" />
                  )}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1">
              <DialogTitle>
                {viewAs === 'organization'
                  ? `${meeting.dancer?.first_name} ${meeting.dancer?.last_name}`
                  : meeting.organization?.name}
              </DialogTitle>
              {meeting.event && (
                <p className="text-muted-foreground text-sm">
                  {meeting.event.name}
                </p>
              )}
            </div>
            {getStatusBadge(meeting.status)}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date and Time */}
          <div className="flex items-start gap-3">
            <Calendar className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">
                {formatDateTime(meeting.scheduled_at)}
              </p>
              <p className="text-muted-foreground text-sm">
                {meeting.duration_minutes} {t('profiles:meetings.minutes')}
              </p>
            </div>
          </div>

          {/* Location */}
          {meeting.location && (
            <div className="flex items-start gap-3">
              <MapPin className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
              <p>{meeting.location}</p>
            </div>
          )}

          {/* Meeting URL */}
          {meeting.meeting_url && (
            <div className="flex items-start gap-3">
              <ExternalLink className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
              <a
                href={meeting.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {t('profiles:meetings.joinMeeting')}
              </a>
            </div>
          )}

          <Separator />

          {/* Priority and Request info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">
                {t('profiles:meetings.details.priority')}
              </p>
              <p className="font-medium">
                {getPriorityLabel(meeting.priority)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">
                {t('profiles:meetings.details.requestedBy')}
              </p>
              <p className="font-medium">
                {getRequestedByLabel(meeting.requested_by)}
              </p>
            </div>
          </div>

          {/* Notes */}
          {meeting.notes && (
            <div>
              <p className="text-muted-foreground mb-1 text-sm">
                {t('profiles:meetings.details.notes')}
              </p>
              <p className="bg-muted rounded-md p-3 text-sm">{meeting.notes}</p>
            </div>
          )}

          {/* Organization Notes (only visible to organization) */}
          {viewAs === 'organization' && meeting.organization_notes && (
            <div>
              <p className="text-muted-foreground mb-1 text-sm">
                {t('profiles:meetings.details.organizationNotes')}
              </p>
              <p className="bg-muted rounded-md p-3 text-sm">
                {meeting.organization_notes}
              </p>
            </div>
          )}

          {/* Timestamps */}
          {(meeting.confirmed_at ||
            meeting.cancelled_at ||
            meeting.completed_at) && (
            <>
              <Separator />
              <div className="text-muted-foreground grid gap-1 text-xs">
                {meeting.confirmed_at && (
                  <p>
                    {t('profiles:meetings.details.confirmedAt')}:{' '}
                    {new Date(meeting.confirmed_at).toLocaleString()}
                  </p>
                )}
                {meeting.cancelled_at && (
                  <p>
                    {t('profiles:meetings.details.cancelledAt')}:{' '}
                    {new Date(meeting.cancelled_at).toLocaleString()}
                  </p>
                )}
                {meeting.completed_at && (
                  <p>
                    {t('profiles:meetings.details.completedAt')}:{' '}
                    {new Date(meeting.completed_at).toLocaleString()}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-2">
            {onCancel && canCancel && (
              <Button
                variant="ghost"
                onClick={() =>
                  handleAction(onCancel, t('profiles:meetings.toast.cancelled'))
                }
                disabled={isPending}
              >
                {isPending ? (
                  <Spinner className="mr-1.5 h-4 w-4" />
                ) : (
                  <XCircle className="mr-1.5 h-4 w-4" />
                )}
                {t('profiles:meetings.actions.cancel')}
              </Button>
            )}
            {onMarkNoShow && canMarkNoShow && (
              <Button
                variant="ghost"
                onClick={() =>
                  handleAction(
                    onMarkNoShow,
                    t('profiles:meetings.toast.noShow'),
                  )
                }
                disabled={isPending}
              >
                {isPending ? (
                  <Spinner className="mr-1.5 h-4 w-4" />
                ) : (
                  <UserMinus className="mr-1.5 h-4 w-4" />
                )}
                {t('profiles:meetings.actions.noShow')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {onConfirm && canConfirm && (
              <Button
                onClick={() =>
                  handleAction(
                    onConfirm,
                    t('profiles:meetings.toast.confirmed'),
                  )
                }
                disabled={isPending}
              >
                {isPending ? (
                  <Spinner className="mr-1.5 h-4 w-4" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                )}
                {t('profiles:meetings.actions.confirm')}
              </Button>
            )}
            {onComplete && canComplete && (
              <Button
                onClick={() =>
                  handleAction(
                    onComplete,
                    t('profiles:meetings.toast.completed'),
                  )
                }
                disabled={isPending}
              >
                {isPending ? (
                  <Spinner className="mr-1.5 h-4 w-4" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                )}
                {t('profiles:meetings.actions.complete')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
